import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import researchService from '../../services/researchService';
import SourcePreviewModal from './SourcePreviewModal';
import DbSourceDetailsModal from './DbSourceDetailsModal';
import ResearchPresetQuestionsManager from './ResearchPresetQuestionsManager';
import ResearchConversationSidebar from './ResearchConversationSidebar';
import '../../pages/Resources.css';

const SEARCH_SOURCE_DEFS = [
  {
    id: 'docs',
    label: 'Docs',
    kb: 'docs',
    docTypes: ['notes', 'earnings', 'earnings_press_release', 'earnings_deck', 'other'],
  },
  {
    id: 'transcripts',
    label: 'Transcripts (YouTube)',
    kb: 'docs',
    docTypes: ['transcript'],
  },
  {
    id: 'xOfficial',
    label: 'Official X Posts',
    kb: 'docs',
    // Keep both spellings for compatibility with historical metadata.
    docTypes: ['post-offical', 'post-official'],
  },
  {
    id: 'xTrusted',
    label: 'Trusted X Posts',
    kb: 'docs',
    docTypes: ['post-trusted'],
  },
  {
    id: 'db',
    label: 'DB',
    kb: 'db',
    docTypes: [],
  },
];

const DEFAULT_SEARCH_SOURCES = SEARCH_SOURCE_DEFS.map((s) => s.id);

/**
 * Chat panel for the Company Research agent. Queries can target the docs KB
 * (uploaded files), the structured DB KB, or both merged together.
 */
export default function ResearchChatPanel({ companyId }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selectedSearchSources, setSelectedSearchSources] = useState(DEFAULT_SEARCH_SOURCES);
  const [previewSource, setPreviewSource] = useState(null); // { s3Uri, filename, sourceUrl }
  const [dbSourcePreview, setDbSourcePreview] = useState(null); // { source, index }
  const [presetQuestions, setPresetQuestions] = useState([]);
  const [showManager, setShowManager] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);

  const refreshConversations = async (nextCompanyId = companyId) => {
    if (!nextCompanyId) {
      setConversations([]);
      return;
    }
    try {
      const list = await researchService.listConversations(nextCompanyId);
      setConversations(list || []);
    } catch {
      setConversations([]);
    }
  };

  useEffect(() => {
    researchService.listPresetQuestions()
      .then(setPresetQuestions)
      .catch(() => {}); // non-fatal
  }, []);

  useEffect(() => {
    setMessages([]);
    setInput('');
    setSessionId(null);
    setActiveConversationId(null);
    setPreviewSource(null);
    setDbSourcePreview(null);
    refreshConversations(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const toggleSearchSource = (sourceId) => {
    setSelectedSearchSources((prev) => (
      prev.includes(sourceId)
        ? prev.filter((id) => id !== sourceId)
        : [...prev, sourceId]
    ));
  };

  const getSearchSummary = () => {
    if (selectedSearchSources.length === 0) return 'No sources selected';
    if (selectedSearchSources.length === SEARCH_SOURCE_DEFS.length) return 'All sources';
    return SEARCH_SOURCE_DEFS
      .filter((s) => selectedSearchSources.includes(s.id))
      .map((s) => s.label)
      .join(' + ');
  };

  const getQueryRouting = () => {
    const selectedDefs = SEARCH_SOURCE_DEFS.filter((s) => selectedSearchSources.includes(s.id));
    const hasDocs = selectedDefs.some((s) => s.kb === 'docs');
    const hasDb = selectedDefs.some((s) => s.kb === 'db');

    const kbScope = hasDocs && hasDb
      ? 'both'
      : hasDocs
        ? 'docs'
        : hasDb
          ? 'db'
          : null;

    const docTypes = hasDocs
      ? [...new Set(selectedDefs.flatMap((s) => s.docTypes || []))]
      : undefined;

    return { kbScope, docTypes };
  };

  const send = async (e) => {
    e.preventDefault();
    const question = input.trim();
    if (!question || !companyId || busy) return;

    const { kbScope, docTypes } = getQueryRouting();
    if (!kbScope) {
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content: 'Error: select at least one search source before sending.',
          error: true,
        },
      ]);
      return;
    }

    const userMsg = { role: 'user', content: question };
    setMessages((m) => [...m, userMsg]);
    setInput('');
    setBusy(true);

    try {
      // Fire async job
      const { query_id } = await researchService.startQuery({
        companyId,
        question,
        sessionId,
        conversationId: activeConversationId,
        kbScope,
        docTypes,
      });

      // Poll until done (up to 5 minutes, 3s interval)
      let resp = null;
      const deadline = Date.now() + 5 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        const poll = await researchService.pollQuery(query_id);
        if (poll.status === 'completed') {
          resp = poll;
          break;
        }
        if (poll.status === 'failed') {
          throw new Error(poll.error || 'Query job failed');
        }
        // still pending — keep polling
      }
      if (!resp) throw new Error('Query timed out after 5 minutes');

      if (resp.session_id) setSessionId(resp.session_id);
      if (resp.conversation_id) setActiveConversationId(resp.conversation_id);
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content: resp.answer || '(no answer)',
          sources: resp.sources || [],
        },
      ]);
      refreshConversations();
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content: `Error: ${err?.response?.data?.detail || err.message || 'unknown'}`,
          error: true,
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const clear = () => {
    setMessages([]);
    setSessionId(null);
    setActiveConversationId(null);
  };

  const startNewConversation = () => {
    setMessages([]);
    setSessionId(null);
    setActiveConversationId(null);
    setInput('');
  };

  const loadConversation = async (conversationId) => {
    if (!companyId || !conversationId || busy) return;
    try {
      const data = await researchService.getConversation(companyId, conversationId);
      setActiveConversationId(conversationId);
      setSessionId(data?.conversation?.session_id || null);
      const restored = (data?.messages || []).map((m) => ({
        role: m.role,
        content: m.content,
        sources: m.sources || [],
      }));
      setMessages(restored);
    } catch (err) {
      setMessages([
        {
          role: 'assistant',
          content: `Error: ${err?.response?.data?.detail || err.message || 'unknown'}`,
          error: true,
        },
      ]);
    }
  };

  const renameConversation = async (conversationId, title) => {
    if (!companyId || !conversationId) return;
    await researchService.renameConversation(companyId, conversationId, title);
    await refreshConversations();
  };

  const deleteConversation = async (conversationId) => {
    if (!companyId || !conversationId) return;
    await researchService.deleteConversation(companyId, conversationId);
    if (conversationId === activeConversationId) {
      startNewConversation();
    }
    await refreshConversations();
  };

  const refreshPresets = () => {
    researchService.listPresetQuestions()
      .then(setPresetQuestions)
      .catch(() => {});
  };

  const getSourceUrlFromMetadata = (metadata) => {
    if (!metadata || typeof metadata !== 'object') return null;

    const priorityKeys = [
      'source',
      'url',
      'source_url',
      'transcript_url',
      'video_url',
      'youtube_url',
      'web_url',
      'webpage_url',
      'original_url',
    ];

    for (const key of priorityKeys) {
      const value = metadata[key];
      if (typeof value === 'string' && /^https?:\/\//i.test(value.trim())) {
        return value.trim();
      }
    }

    for (const [key, value] of Object.entries(metadata)) {
      if (!/url/i.test(key)) continue;
      if (typeof value === 'string' && /^https?:\/\//i.test(value.trim())) {
        return value.trim();
      }
    }

    return null;
  };

  const normalizeTypeToken = (value) => String(value || '').trim().toLowerCase();

  const detectSourceType = (source) => {
    const metadata = (source && source.metadata && typeof source.metadata === 'object')
      ? source.metadata
      : {};

    const candidateValues = [
      metadata.source_type,
      metadata.type,
      metadata.document_type,
      metadata.doc_type,
      metadata.content_type,
      metadata.category,
      metadata.source,
    ]
      .map(normalizeTypeToken)
      .filter(Boolean);

    const isTranscriptType = candidateValues.some((v) =>
      v === 'transcript' || v.includes('youtube transcript') || v.includes('video transcript')
    );
    if (isTranscriptType) return 'transcript';

    const isXPostType = candidateValues.some((v) =>
      v === 'x-post' ||
      v === 'x post' ||
      v === 'x_post' ||
      v === 'xpost' ||
      v.includes('x-post') ||
      v.includes('x post') ||
      v.includes('x/tweet') ||
      v.includes('tweet')
    );
    if (isXPostType) return 'x-post';

    // Fallback for legacy rows where explicit type is missing.
    const s3Path = normalizeTypeToken(source?.s3_uri || '');
    if (s3Path.includes('/transcripts/') || s3Path.includes('youtube')) return 'transcript';
    if (s3Path.includes('/x-posts/') || s3Path.includes('/x_posts/') || s3Path.includes('/xposts/')) return 'x-post';

    return 'default';
  };

  const getSourceIconProps = (sourceType) => {
    if (sourceType === 'transcript') {
      return {
        className: 'bi bi-play-btn-fill',
        style: { color: '#FF0000' },
        title: 'Transcript source',
      };
    }
    if (sourceType === 'x-post') {
      return {
        className: 'bi bi-twitter-x',
        style: undefined,
        title: 'X-Post source',
      };
    }
    return {
      className: 'bi bi-file-earmark-text',
      style: undefined,
      title: 'Document source',
    };
  };

  return (
    <div className="card" style={{ minWidth: 0 }}>
      <div className="card-header d-flex justify-content-between align-items-center">
        <span className="fw-bold"><i className="bi bi-chat-dots me-2" />Ask the research agent</span>
        <div className="d-flex align-items-center gap-2">
          <label className="small mb-0">Search:</label>
          <details className="position-relative">
            <summary
              className="btn btn-sm btn-outline-secondary"
              style={{ minWidth: 190, textAlign: 'left' }}
            >
              {getSearchSummary()}
            </summary>
            <div
              className="card position-absolute end-0 mt-1 p-2"
              style={{ minWidth: 270, zIndex: 20 }}
            >
              {SEARCH_SOURCE_DEFS.map((source) => (
                <label
                  key={source.id}
                  className="form-check d-flex align-items-center mb-2"
                  style={{ cursor: 'pointer' }}
                >
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={selectedSearchSources.includes(source.id)}
                    onChange={() => toggleSearchSource(source.id)}
                  />
                  <span className="ms-2 small">{source.label}</span>
                </label>
              ))}
            </div>
          </details>
          <button className="btn btn-sm btn-outline-secondary" onClick={clear} disabled={busy}>
            Clear
          </button>
          <button
            className="btn btn-sm btn-outline-secondary"
            title="Manage preset questions"
            onClick={() => setShowManager(true)}
          >
            <i className="bi bi-gear" />
          </button>
        </div>
      </div>
      <div className="card-body p-0 d-flex" style={{ minWidth: 0 }}>
        <ResearchConversationSidebar
          conversations={conversations}
          activeConversationId={activeConversationId}
          busy={busy || !companyId}
          onSelect={loadConversation}
          onCreateNew={startNewConversation}
          onRename={renameConversation}
          onDelete={deleteConversation}
        />

        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <div style={{ height: 460, overflowY: 'scroll', overflowX: 'hidden', padding: '1rem' }}>
          {messages.length === 0 && (
            <div className="text-muted small">
              Ask a question — e.g. "What was revenue last quarter?" or "Summarize recent analyst notes."
            </div>
          )}
          {messages.map((m, i) => (
            <div
              key={i}
              className={`mb-3 p-2 rounded ${m.role === 'user' ? 'bg-light' : 'bg-white border'}`}
            >
              <div className="small text-uppercase text-muted mb-1">
                {m.role === 'user' ? 'You' : 'Agent'}
              </div>
              <div className="markdown-content" style={{ overflowX: 'auto' }}>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm, remarkBreaks]}
                  components={{
                    table: ({ node, ...props }) => (
                      <table className="table table-sm table-bordered table-striped" style={{ fontSize: '0.85rem' }} {...props} />
                    ),
                    thead: ({ node, ...props }) => <thead className="table-light" {...props} />,
                  }}
                >{m.content}</ReactMarkdown>
              </div>
              {m.sources && m.sources.length > 0 && (() => {
                // Deduplicate by s3_uri, then separate docs vs db
                const seen = new Set();
                const unique = m.sources.filter((s) => {
                  const key = s.s3_uri || `db-${seen.size}`;
                  if (seen.has(key)) return false;
                  seen.add(key);
                  return true;
                });
                const docSources = unique.filter((s) => s.knowledge_base === 'docs' && s.s3_uri);
                const dbSources = unique.filter((s) => s.knowledge_base === 'db');
                return (
                  <details className="mt-2">
                    <summary className="small text-muted">
                      {unique.length} source{unique.length === 1 ? '' : 's'}
                    </summary>
                    <div className="mt-2 d-flex flex-wrap gap-2">
                      {docSources.map((s, j) => {
                        const filename = s.s3_uri.split('/').pop() || s.s3_uri;
                        const sourceUrl = getSourceUrlFromMetadata(s.metadata);
                        const sourceType = detectSourceType(s);
                        const iconProps = getSourceIconProps(sourceType);
                        return (
                          <div key={j} className="d-flex align-items-center gap-2 flex-wrap">
                            <button
                              className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                              style={{ fontSize: '0.78rem' }}
                              onClick={() => setPreviewSource({ s3Uri: s.s3_uri, filename, sourceUrl })}
                            >
                              <i className={iconProps.className} style={iconProps.style} title={iconProps.title} />
                              {filename}
                            </button>
                            {sourceUrl && sourceType !== 'x-post' && (
                              <a
                                href={sourceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="small"
                                style={{ fontSize: '0.78rem' }}
                                title={sourceUrl}
                              >
                                <i className="bi bi-box-arrow-up-right me-1" />
                                Open Video
                              </a>
                            )}
                          </div>
                        );
                      })}
                      {dbSources.length > 0 && (
                        dbSources.map((s, j) => (
                          <button
                            key={`db-${j}`}
                            className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                            style={{ fontSize: '0.78rem' }}
                            onClick={() => setDbSourcePreview({ source: s, index: j + 1 })}
                            title="View DB source details"
                          >
                            <i className="bi bi-database" />
                            DB record {j + 1}
                          </button>
                        ))
                      )}
                    </div>
                  </details>
                );
              })()}
            </div>
          ))}
          {busy && <div className="text-muted small">Thinking…</div>}
          </div>
        </div>
      </div>
      {previewSource && (
        <SourcePreviewModal
          s3Uri={previewSource.s3Uri}
          filename={previewSource.filename}
          sourceUrl={previewSource.sourceUrl}
          onClose={() => setPreviewSource(null)}
        />
      )}
      {dbSourcePreview && (
        <DbSourceDetailsModal
          source={dbSourcePreview.source}
          index={dbSourcePreview.index}
          onClose={() => setDbSourcePreview(null)}
        />
      )}
      {showManager && (
        <ResearchPresetQuestionsManager
          onClose={() => { setShowManager(false); refreshPresets(); }}
        />
      )}
      {presetQuestions.length > 0 && (
        <div className="card-footer py-2 d-flex align-items-center gap-2 flex-wrap border-top-0">
          <span className="small text-muted" style={{ whiteSpace: 'nowrap' }}>Quick:</span>
          {presetQuestions.map((q) => (
            <button
              key={q.question_id}
              type="button"
              className="btn btn-sm btn-outline-primary"
              style={{ fontSize: '0.78rem' }}
              disabled={!companyId || busy}
              onClick={() => setInput(q.text)}
            >
              {q.label || q.text}
            </button>
          ))}
        </div>
      )}
      <form className="card-footer d-flex gap-2" onSubmit={send}>
        <input
          className="form-control"
          placeholder={companyId ? 'Ask a question…' : 'Select a company first'}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={!companyId || busy}
        />
        <button
          className="btn btn-primary"
          type="submit"
          disabled={!companyId || busy || !input.trim() || selectedSearchSources.length === 0}
        >
          Send
        </button>
      </form>
    </div>
  );
}
